import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import "swiper/css/navigation";
import "swiper/css/pagination";
import React, { useEffect, memo } from 'react'
import { Navigation, Pagination, Autoplay } from 'swiper';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import PlayerMedia from '../PlayerMedia/PlayerMedia';

// Utility function to check if URL is a video
const isVideoUrl = (url) => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mkv', '.mov', '.wmv', '.flv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

// Component to render media (image only for main display)
const MediaItemComponent = ({ src, className, width, height, alt, isLightboxEnabled = true, galleryID, index, widthPhoto, heightPhoto, uniqueKey }) => {
    const isVideo = isVideoUrl(src);

    // Only show images in main display
    if (isVideo) return null;

    if (isLightboxEnabled) {
        return (
            <a
                href={src}
                data-pswp-width={widthPhoto}
                data-pswp-height={heightPhoto}
                data-pswp-type="image"
                data-pswp-id={uniqueKey}
                key={uniqueKey}
                target="_blank"
                rel="noreferrer"
            >
                <img
                    className={className}
                    src={src}
                    width={width}
                    height={height}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                />
            </a>
        );
    }

    return <img className={className} src={src} width={width} height={height} alt={alt} loading="lazy" decoding="async" />;
};

// memoize to avoid re-renders
const MediaItem = memo(MediaItemComponent);

export default function PictureSlider({ data }) {
    const galleryID = "photo-gallery"
    const videoGalleryID = "video-gallery"
    const widthPhoto = 800
    const heightPhoto = 600

    // Separate photos and videos
    const photos = data.unit?.media?.filter(item => !isVideoUrl(item)) || [];
    const videos = data.unit?.media?.filter(item => isVideoUrl(item)) || [];

    useEffect(() => {
        if (!data?.unit?.media || data.unit.media.length === 0) return;

        let photoLightbox = null;
        let videoLightbox = null;
        let PhotoSwipeLightbox = null;

        // dynamic import to reduce initial bundle cost
        (async () => {
            try {
                const mod = await import('photoswipe/lightbox');
                PhotoSwipeLightbox = mod.default;
                photoLightbox = new PhotoSwipeLightbox({
                    gallery: '#' + galleryID,
                    children: 'a',
                    initialZoomLevel: "fit",
                    secondaryZoomLevel: 1.5,
                    maxZoomLevel: 1,
                    pswpModule: () => import('photoswipe'),
                });
                videoLightbox = new PhotoSwipeLightbox({
                    gallery: '#' + videoGalleryID,
                    children: 'a',
                    initialZoomLevel: "fit",
                    secondaryZoomLevel: 1.5,
                    maxZoomLevel: 1,
                    pswpModule: () => import('photoswipe'),
                });

                const setupVideoFilter = (lightbox) => {
                    lightbox.addFilter('itemData', (itemData) => {
                        const element = itemData.element;
                        const isVideo = element?.getAttribute('data-pswp-type') === 'video';
                        const uniqueId = element?.getAttribute('data-pswp-id');

                        if (isVideo && uniqueId) {
                            return {
                                ...itemData,
                                html: `
                                    <div class="pswp__video-container" style="display:flex;align-items:center;justify-content:center;height:100%;width:100%;">
                                        <video controls autoplay style="object-fit:contain;border-radius:20px;height:100%;width:100%;" src="${itemData.src}">
                                            <source src="${itemData.src}" type="video/mp4">
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                `
                            };
                        }
                        return itemData;
                    });

                    lightbox.on('contentLoad', (e) => {
                        const { content } = e;
                        if (content?.data?.html) {
                            e.preventDefault();
                            content.element = document.createElement('div');
                            content.element.innerHTML = content.data.html;
                        }
                    });

                    lightbox.on('destroy', () => {
                        const vids = document.querySelectorAll('.pswp__video-container video');
                        vids.forEach(v => { v.pause(); v.currentTime = 0; });
                    });
                };

                setupVideoFilter(videoLightbox);
                photoLightbox.init();
                videoLightbox.init();
            } catch (err) {
                console.error('PhotoSwipe init error', err);
            }
        })();

        return () => {
            try {
                photoLightbox?.destroy();
                videoLightbox?.destroy();
                photoLightbox = null;
                videoLightbox = null;
            } catch (e) { /* ignore cleanup errors */ }
        };
    }, [galleryID, videoGalleryID, data?.unit?.media]);

    // Function to open video lightbox programmatically
    const openVideoLightbox = () => {
        if (videos.length === 0) return;
        const videoContainer = document.getElementById(videoGalleryID);
        if (!videoContainer) return;
        const firstVideoLink = videoContainer.querySelector('a');
        firstVideoLink?.click();
    };

    // Early return if no media
    if (!data?.unit?.media || data.unit.media.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <p>لا توجد وسائط متاحة</p>
            </div>
        );
    }

    return (
        <>
            {/* Desktop - Photos Only */}
            <div className='row g-4 image-unit d-none d-md-flex' id={galleryID}>
                <Swiper
                    spaceBetween={50}
                    slidesPerView={1}
                    autoplay={{
                        delay: 10000,
                        disableOnInteraction: false,
                    }}
                    dir="rtl"
                    loop={photos.length > 1}
                    navigation={true}
                    pagination={{ clickable: true }}
                    modules={[Navigation, Pagination, Autoplay]}
                    className="mySwiper"
                    style={{
                        "--swiper-navigation-color": "#333333",
                        "--swiper-pagination-color": "#074acc",
                    }}
                >
                    {photos.map((ele, i, photoArray) => {
                        if (i % 5 === 0) {
                            return (
                                <SwiperSlide key={`photo-slide-${i}`}>
                                    <div className='row g-4 d-none d-md-flex pb-4'>
                                        <div className="col-6 d-none d-md-inline-block">
                                            {photoArray[i] && (
                                                <MediaItem
                                                    src={photoArray[i]}
                                                    className='w-100 resize-main-image border-img'
                                                    alt="units"
                                                    isLightboxEnabled={true}
                                                    galleryID={galleryID}
                                                    index={i}
                                                    uniqueKey={`photo-${i}`}
                                                    widthPhoto={widthPhoto}
                                                    heightPhoto={heightPhoto}
                                                />
                                            )}
                                        </div>
                                        <div className="col-3">
                                            {photoArray[i + 1] && (
                                                <div className="mb-4">
                                                    <MediaItem
                                                        src={photoArray[i + 1]}
                                                        className='w-100 resize-second-image border-img'
                                                        alt="unit"
                                                        isLightboxEnabled={true}
                                                        galleryID={galleryID}
                                                        index={i + 1}
                                                        uniqueKey={`photo-${i + 1}`}
                                                        widthPhoto={widthPhoto}
                                                        heightPhoto={heightPhoto}
                                                    />
                                                </div>
                                            )}
                                            {photoArray[i + 2] && (
                                                <MediaItem
                                                    src={photoArray[i + 2]}
                                                    className='w-100 resize-second-image border-img'
                                                    alt="unit"
                                                    isLightboxEnabled={true}
                                                    galleryID={galleryID}
                                                    index={i + 2}
                                                    uniqueKey={`photo-${i + 2}`}
                                                    widthPhoto={widthPhoto}
                                                    heightPhoto={heightPhoto}
                                                />
                                            )}
                                        </div>
                                        <div className="col-3">
                                            {photoArray[i + 3] && (
                                                <div className="mb-4">
                                                    <MediaItem
                                                        src={photoArray[i + 3]}
                                                        className='w-100 resize-second-image border-img'
                                                        alt="unit"
                                                        isLightboxEnabled={true}
                                                        galleryID={galleryID}
                                                        index={i + 3}
                                                        uniqueKey={`photo-${i + 3}`}
                                                        widthPhoto={widthPhoto}
                                                        heightPhoto={heightPhoto}
                                                    />
                                                </div>
                                            )}
                                            {photoArray[i + 4] && (
                                                <MediaItem
                                                    src={photoArray[i + 4]}
                                                    className='w-100 resize-second-image border-img'
                                                    alt="unit"
                                                    isLightboxEnabled={true}
                                                    galleryID={galleryID}
                                                    index={i + 4}
                                                    uniqueKey={`photo-${i + 4}`}
                                                    widthPhoto={widthPhoto}
                                                    heightPhoto={heightPhoto}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </SwiperSlide>
                            );
                        }
                        return null;
                    })}
                </Swiper>
            </div>

            {/* Mobile - Photos Only */}
            <div className="d-md-none" id={galleryID + '-mobile'}>
                <Swiper
                    spaceBetween={50}
                    slidesPerView={1}
                    dir="rtl"
                    // autoplay={{
                    //     delay: 10000,
                    //     disableOnInteraction: false,
                    // }}
                    loop={false}
                    navigation={true}
                    pagination={{ clickable: true }}
                    modules={[Navigation, Pagination, Autoplay]}
                    className="mySwiper"
                    style={{
                        "--swiper-navigation-color": "#000",
                        "--swiper-pagination-color": "#074acc",
                    }}
                >
                    {photos.map((ele, i) => (
                        <SwiperSlide key={`mobile-photo-slide-${i}`}>
                            <div className='h-100 pb-4'>
                                <MediaItem
                                    src={ele}
                                    className='w-100 border-img'
                                    width={400}
                                    height={224}
                                    alt="unit"
                                    isLightboxEnabled={true}
                                    galleryID={galleryID + '-mobile'}
                                    index={i}
                                    uniqueKey={`mobile-photo-${i}`}
                                    widthPhoto={widthPhoto}
                                    heightPhoto={heightPhoto}
                                />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* Videos Button */}
            {videos.length > 0 && (
                <div className="text-start mt-3">
                    <button
                        className="btn btn-primary btn-lg"
                        onClick={openVideoLightbox}
                        style={{
                            backgroundColor: '#074acc',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '10px 30px',
                            fontSize: '16px',
                        }}
                    >
                        عرض الفيديوهات ({videos.length})
                    </button>
                </div>
            )}

            {/* Hidden Video Gallery for Lightbox */}
            <div style={{ display: 'none' }} id={videoGalleryID}>
                {videos.map((video, index) => (
                    <a
                        key={`video-${index}`}
                        href={video}
                        data-pswp-width={widthPhoto}
                        data-pswp-height={heightPhoto}
                        data-pswp-type="video"
                        data-pswp-id={`video-${index}`}
                    >
                        {/* Hidden video links for lightbox */}
                    </a>
                ))}
            </div>
        </>
    )
}
